import { apiRequest } from "./api.client";

// ── Types (mirror app/api/v1/endpoints/platform_ops.py) ───────────────────────

/**
 * One clinic's contribution over the window, from the GST documents we issued.
 *
 * net is the taxable value and is the revenue figure. gross adds GST, which is
 * collected on the government's behalf and remitted, so reading gross as
 * revenue overstates the business by the tax rate; it is here because gross is
 * what reconciles against a bank statement.
 */
export interface ClinicRevenue {
  clinic_id: string;
  clinic_name: string | null;
  clinic_gstin: string | null;
  net_paise: number;
  gross_paise: number;
  gst_paise: number;
  subscription_net_paise: number;
  credit_topup_net_paise: number;
  document_count: number;
}

/** GET /platform/revenue. What we RECOGNISED, which is a subset of what we received. */
export interface RevenueReport {
  window_start: string;
  window_end: string;
  net_paise: number;
  gross_paise: number;
  gst_paise: number;
  clinic_count: number;
  clinics: ClinicRevenue[];
}

/** One payment Razorpay captured that reached nothing on our side. */
export interface UnrecordedPayment {
  kind: string;
  severity: string;
  detail: string;
  context: {
    payment_id: string;
    order_id: string | null;
    amount_paise: number | null;
    created_at: number | null;
    notes_keys: string[];
  };
}

/** GET /platform/billing/reconcile-razorpay. What we RECEIVED versus what we recorded. */
export interface RazorpayReconcileReport {
  window_start: string;
  window_end: string;
  unrecorded_count: number;
  unrecorded_paise: number;
  findings: UnrecordedPayment[];
}

export const PlatformRevenueAPI = {
  revenue(days: number): Promise<RevenueReport> {
    return apiRequest<RevenueReport>(`/platform/revenue?days=${days}`);
  },

  /**
   * Reaches Razorpay, so this one can fail when Razorpay is down. It answers
   * 502 rather than an empty sweep in that case, deliberately: "nothing
   * missing" is the wrong thing to show when we could not actually look.
   */
  reconcileRazorpay(days: number): Promise<RazorpayReconcileReport> {
    return apiRequest<RazorpayReconcileReport>(`/platform/billing/reconcile-razorpay?days=${days}`);
  },
};
